import { NavLink, useLocation } from 'react-router-dom';
import type { KeyboardEvent } from 'react';
import { brand } from '../theme/tokens';
import { brandAssets } from '../theme/brand-assets';
import { useLanguage } from './language-context';
import { navGroups, navItemForPath } from './nav-items';
import type { NavGroupId, NavItem } from './nav-items';
import { NavIcon } from './nav-icon';
import { useRailGroups } from './rail-groups';
import { badgeLabel } from './use-rail-badges';
import type { RailBadges } from './use-rail-badges';
import { appVersion, appVersionDetail } from './app-build';
import { SignOutButton } from './sign-out-button';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { localiseDigits } from '@bookingdei/i18n/format-number';

// CB-17 rail (design D:159-221, script D:8428-8481): logo lockup + version,
// console tag + collapse-all, grouped nav with live badges, collapsible group
// headers (click toggles, double-click focuses, Enter/Space toggles; state kept in
// localStorage by rail-groups.ts), footer note and hold-to-confirm sign-out. The
// group that holds the current screen is always open. Shown at ≥ 1200 px; below
// that the section-chip row takes over (DR-45). Omitted from the design: the
// maker-credit flip card (not shipped, INF-1) and the Help / Contact card (help
// centre deferred, DR-43 — DC110).

export function Rail({
  items,
  badges,
  consoleTag,
}: {
  items: NavItem[];
  badges: RailBadges;
  consoleTag: string;
}) {
  const { t, lang } = useLanguage();
  const { pathname } = useLocation();
  const { collapsed, toggle, focus, toggleAll } = useRailGroups();
  const active = navItemForPath(pathname);

  const groups = navGroups
    .map((g) => ({ ...g, items: items.filter((i) => i.group === g.group) }))
    .filter((g) => g.items.length > 0);
  const collapsible = groups.map((g) => g.group).filter((g) => g !== 'main');
  const allCollapsed = collapsible.length > 0 && collapsible.every((g) => collapsed.has(g));
  const num = (n: number) => localiseDigits(lang, n);

  const isOpen = (g: NavGroupId) => g === 'main' || active?.group === g || !collapsed.has(g);

  const onHeadKey = (e: KeyboardEvent, g: NavGroupId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle(g);
    }
  };

  return (
    <aside className="rail" aria-label={t('admin.nav.primary')}>
      <div className="rail__brand">
        <div className="rail__lockup">
          <img className="rail__logo" src={brandAssets.lockup} alt={brand.name} width={139} height={26} />
          <img className="rail__logo rail__logo--rev" src={brandAssets.lockupReversed} alt="" aria-hidden="true" width={139} height={26} />
          {appVersion && (
            <span className="rail__version" translate="no" title={formatMessage(lang, 'admin.shell.version', { version: appVersionDetail ?? appVersion })}>
              V.{appVersion}
            </span>
          )}
        </div>
      </div>
      <div className="rail__tagrow">
        <span className="rail__tag">
          <NavIcon name="shield" size={11} />
          {consoleTag}
        </span>
        <span className="rail__spacer" />
        <button
          type="button"
          className={allCollapsed ? 'rail__all is-collapsed' : 'rail__all'}
          onClick={() => toggleAll(collapsible)}
          aria-label={t(allCollapsed ? 'admin.shell.expand_all' : 'admin.shell.collapse_all')}
          title={t(allCollapsed ? 'admin.shell.expand_all' : 'admin.shell.collapse_all')}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path className="rail__all-top" d="M8 5.5L12 9.5L16 5.5" />
            <path className="rail__all-mid" d="M5 12H19" />
            <path className="rail__all-bot" d="M8 18.5L12 14.5L16 18.5" />
          </svg>
        </button>
      </div>

      <nav className="rail__nav">
        {groups.map((g, i) => {
          const open = isOpen(g.group);
          const sum = g.items.reduce((a, it) => a + (badges[it.section] ?? 0), 0);
          const listId = `rail-group-${g.group}`;
          return (
            <div key={g.group} className="rail__group">
              {i > 0 && <div className="rail__divider" />}
              {g.group !== 'main' && (
                <div
                  role="button"
                  tabIndex={0}
                  className="rail__head"
                  aria-expanded={open}
                  aria-controls={listId}
                  onClick={() => toggle(g.group)}
                  onDoubleClick={() => focus(g.group)}
                  onKeyDown={(e) => onHeadKey(e, g.group)}
                >
                  <span className={open ? 'rail__chev' : 'rail__chev is-closed'} aria-hidden="true">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </span>
                  <span className="rail__head-label">{t(g.label)}</span>
                  {!open && sum > 0 && (
                    <span className="rail__head-badge" title={formatMessage(lang, 'admin.shell.group_badge', { n: sum })}>
                      {num(sum)}
                    </span>
                  )}
                </div>
              )}
              <div id={listId} className={open ? 'rail__items' : 'rail__items is-closed'} hidden={!open}>
                {g.items.map((item) => {
                  const n = badges[item.section] ?? 0;
                  const label = badgeLabel[item.section];
                  const title = n > 0 && label ? `${num(n)} ${t(label)}` : undefined;
                  return (
                    <NavLink
                      key={item.section}
                      to={item.to}
                      end={item.to === '/'}
                      className={({ isActive }) => (isActive ? 'rail__link is-active' : 'rail__link')}
                    >
                      <span className="rail__icon">
                        <NavIcon name={item.icon} />
                      </span>
                      <span className="rail__label">{t(item.key)}</span>
                      {n > 0 && (
                        <span className="rail__badge" title={title} aria-label={title}>
                          {num(n)}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <p className="rail__note">{t('admin.audit_note')}</p>
      <SignOutButton />
    </aside>
  );
}
